'use client';

import { useId, type Dispatch } from 'react';
import type { DoorLook, DoorStyle } from '../drawing/door';
import { SvgArt } from '../drawing/SvgArt';
import { PIECE_LABEL, type HandleProduct, type Piece } from './drilling';
import type { DoorOrderAction, DoorView } from './orderState';
import { backCaption, backSvg, frontSvg } from './views';

const VIEWS: Array<[DoorView, string]> = [
  ['front', 'Front'],
  ['back', 'Back & drilling'],
  ['all', 'Whole order'],
];

/**
 * The live picture: the selected piece from the front, its back with the real
 * drilling, or the whole order to scale (select a piece there to edit it).
 */
export function DoorViewer({
  view,
  pieces,
  active,
  style,
  look,
  colour,
  wood,
  product,
  tag,
  build,
  dispatch,
}: {
  view: DoorView;
  pieces: Piece[];
  active: number;
  style: DoorStyle;
  look?: DoorLook;
  colour: string;
  wood: boolean;
  product: HandleProduct;
  tag: string;
  build: string;
  dispatch: Dispatch<DoorOrderAction>;
}) {
  const id = useId();
  const piece = pieces[active] ?? pieces[0];
  if (!piece) return null;
  const pieceTag = `#${active + 1} · ${piece.h} × ${piece.w} mm · ${PIECE_LABEL[piece.kind]}`;

  let canvas;
  let caption: string;
  if (view === 'front') {
    const box = 430;
    const scale = Math.min(box / Math.max(piece.h, 1), (box * 1.25) / Math.max(piece.w, 1));
    canvas = (
      <SvgArt
        className="sk-db__front"
        style={{ width: `${Math.max(1, piece.w * scale)}px`, height: `${Math.max(1, piece.h * scale)}px` }}
        svg={frontSvg(piece, { style, look, colour, wood, product, idPrefix: `${id}f` })}
      />
    );
    caption = `${pieceTag}${piece.hinge.side !== 'none' ? ` · hinges ${piece.hinge.side}` : ''}`;
  } else if (view === 'back') {
    canvas = <SvgArt className="sk-db__back" svg={backSvg(piece, { colour, idPrefix: `${id}b` })} />;
    caption = `${pieceTag} · ${backCaption(piece)}`;
  } else {
    const tallest = Math.max(...pieces.map((p) => p.h), 1);
    const scale = Math.min(380 / tallest, 0.3);
    canvas = (
      <ul className="sk-db__lineup" aria-label="Every piece, to scale">
        {pieces.map((p, i) => (
          <li key={i}>
            <button type="button" aria-pressed={i === active} onClick={() => dispatch({ type: 'select', index: i })}>
              <SvgArt
                style={{ display: 'block', width: `${Math.max(1, p.w * scale)}px`, height: `${Math.max(1, p.h * scale)}px` }}
                svg={frontSvg(p, { style, look, colour, wood, product, idPrefix: `${id}a${i}`, label: `Piece ${i + 1}` })}
              />
              <span className="sk-db__cap">
                {`#${i + 1} ${p.h}×${p.w}`}
                <span className="sk-db__qty">{`×${p.qty}`}</span>
              </span>
            </button>
          </li>
        ))}
      </ul>
    );
    caption = `${pieces.reduce((sum, p) => sum + p.qty, 0)} pieces, shown to scale · select one to edit it`;
  }

  return (
    <div className="sk-db__stage">
      <span className="sk-db__tag">{tag}</span>
      <div className="sk-seg sk-db__viewseg" role="group" aria-label="Viewer">
        {VIEWS.map(([v, label]) => (
          <button key={v} type="button" aria-pressed={v === view} onClick={() => dispatch({ type: 'view', view: v })}>
            {label}
          </button>
        ))}
      </div>
      <div className="sk-db__canvas">{canvas}</div>
      <div className="sk-db__dims">
        <span aria-live="polite">{caption}</span>
        <span>{build}</span>
      </div>
    </div>
  );
}
